import * as Es6Promise from 'es6-promise';
import { each } from 'lodash';
import * as React from 'react';
import { render } from 'react-dom';
import { Provider } from 'react-redux';
import { HashRouter, Route, Switch } from 'react-router-dom';
import { Store, applyMiddleware, createStore, compose } from 'redux';
import thunk from 'redux-thunk';
import { checkAndApplyPromo, receiveSettings } from './actions/settings-fetch';
import { defaultCartModel } from './cart/reducers/cart-reducer';
//import App from './app';
//import Filter from './filter/containers/filter';
//import Seatmap from './seatmap/containers/seatmap';
import { ItemType } from './classes/item-type';
import asyncComponent from './components/async-component';
import { selectPriceTypeTickets } from './filter/actions/filter-actions';
import { defaultSeatMatchResultModel } from './filter/reducers/fetch-reducer';
import { defaultAdditionalSeatingOptionModel, defaultErrorModel, defaultPerformanceDatesAndTimeModel, defaultPriceRangeModel, defaultPriceTypeFilterModel, defaultVenueLevelModel } from './filter/reducers/filter-reducers';
import { defaultPromoCodeModel } from './filter/reducers/promo-reducer';
import { logger } from './helpers/middleware';
import { getParameterByName } from './helpers/query-string-helpers';
import { defaultApplicationModel } from './reducers/app-reducer';
import { defaultAppSettingModel, IAppSettingsModel } from './reducers/fetch-settings-reducer';
import { IAppState, appReducers } from './reducers/index';
import { defaultSeatMapResultModel } from './seatmap/reducers/fetch-reducer';
import { defaultSeatMapModel } from './seatmap/reducers/seatmap-reducer';
import { defaultExchangeSettingsModel } from './reducers/exchange-reducer';
import { getCartTimer } from './cart/actions/cart-fetch';

const AsyncApp = asyncComponent(() => import("./app"));
const AsyncFilter = asyncComponent(() => import("./filter/containers/filter"));
const AsyncPackageTicketSelect = asyncComponent(() => import("./ticket-selection/containers/package-quantity-select"));
const AsyncSeatmap = asyncComponent(() => import("./seatmap/containers/seatmap"));

import Modal from './common/modal/modal';

//if (process.env.NODE_ENV !== 'production') {
//    const { whyDidYouUpdate } = require('why-did-you-update');
//    whyDidYouUpdate(React);
//}

// To add to window
declare global {
    const Promise: Es6Promise.Promise<any>;
}

if (!(window as any).Promise) {
    (window as any).Promise = Es6Promise;
}

let initialState: IAppState = {
    cartModel: defaultCartModel,
    additionalSeatingOptionModel: defaultAdditionalSeatingOptionModel,
    applicationModel: defaultApplicationModel,
    errorModel: defaultErrorModel,
    seatMapResultModel: defaultSeatMapResultModel,
    promoCodeModel: defaultPromoCodeModel,
    appSettingsModel: defaultAppSettingModel,
    performanceDatesAndTimesModel: defaultPerformanceDatesAndTimeModel,
    priceRangeModel: defaultPriceRangeModel,
    priceTypesModel: defaultPriceTypeFilterModel,
    seatMapModel: defaultSeatMapModel,
    seatMatchResultModel: defaultSeatMatchResultModel,
    venueLevelModel: defaultVenueLevelModel,
    exchangeSettingsModel: defaultExchangeSettingsModel
}

const composeEnhancers = (window as any).__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;
const appStore: Store<IAppState> = createStore(appReducers, initialState as any, composeEnhancers(
    applyMiddleware(thunk, logger)
))

if ((window as any).appSettings.length > 0) {
    try {
        const settingsString = decodeURI((window as any).appSettings);
        getAppSettings(JSON.parse(settingsString));
        console.info("%cSmartSeat AppSettings:", "color:green; font-weight:bold; background-color:yellow", JSON.parse(settingsString));
    } catch (ex) {
        try {
            console.info("Error decoding App Settings: " + ex);
            getAppSettings(JSON.parse((window as any).appSettings));
            console.info("%cSmartSeat AppSettings:", "color:green; font-weight:bold; background-color:yellow", JSON.parse((window as any).appSettings));
        } catch (ex1) {
            console.error("Error parsing App Settings: " + ex1);
        }
    }
} else {
    console.error("App Settings as not bootstrapped onto page");
}

let appSettings = appStore.getState().appSettingsModel;
if (appSettings.displayCartTimer) {
    (appStore.dispatch as any)(getCartTimer());
}

// At this point, react has finished loading so this is the correct place to put this.
let reactLoader = document.getElementById("react-loader");

if (reactLoader) {
    reactLoader.classList.add("loaded");
}

const onlySYOS = appSettings.exchangeTickets.length > 0 || appSettings.isSelectSeatVenue;

render((
    <Provider store={appStore}>
        <HashRouter>
            <div>
                {renderNotOnSaleModal()}

                <Route component={AsyncApp} />
                <Switch>
                    <Route exact path="/" component={onlySYOS ? AsyncSeatmap : AsyncFilter} />
                    <Route path="/ticket-select" component={AsyncPackageTicketSelect} />
                    {
                        onlySYOS ?
                            null
                            : <Route path="/filters" component={AsyncFilter} />
                    }

                    <Route path="/seatmap" component={AsyncSeatmap} />
                    <Route path="*" component={AsyncFilter} />
                </Switch>
            </div>
        </HashRouter>
    </Provider>
), document.getElementById('app-container'))

function renderNotOnSaleModal() {
    const { promoCode, applying } = appStore.getState().promoCodeModel;
    const { itemOnSale, promoCodeOnly } = appSettings;

    // If the item isn't on sale AND
    // It isn't a promo code only item OR we already input a promo code
    // Tell the user they're out
    if (!itemOnSale && (!promoCodeOnly || (promoCode && !applying))) {
        return (
            <Modal modalKey="performance not on sale" isError={true}
                title={appSettings.performanceNotOnSaleModalTitle}
                message={appSettings.performanceNotOnSaleModalMessage}
                confirmFunction={_performanceNotOnSaleRedirect}
                confirmString={appSettings.performanceNotOnSaleRedirectButtonText}
                closeFunction={_performanceNotOnSaleRedirect} />
        )
    }
}

function _performanceNotOnSaleRedirect() {
    window.location.href = appSettings.performanceNotOnSaleRedirectUrl;
}

function getAppSettings(json: IAppSettingsModel) {
    switch (json.itemType) {
        case ItemType.Flex:
            return (appStore.dispatch as any)(loadSettings(json, true));
        case ItemType.Fixed:
            return (appStore.dispatch as any)(loadSettings(json, false));
        case ItemType.Super:
            return (appStore.dispatch as any)(loadSettings(json, true));
        default:
            return (appStore.dispatch as any)(loadSettings(json, false));
    }
}

function loadSettings(json, loadPackageQuantity: boolean) {
    return (dispatch, state: () => any) => {
        dispatch(receiveSettings(json));

        // This MUST happen after recieveSettings
        const newState = state();
        //if (loadPackageQuantity) {
        //    var packageTicketQuantity = (window as any).packageTicketQuantity;
        //    if (packageTicketQuantity !== undefined && packageTicketQuantity.length > 0) {
        //        var ticketQuantityObject = JSON.parse(packageTicketQuantity);
        //dispatch(setPackageTicketQuantity(packageTicketQuantity));

        //each(ticketQuantityObject, (ticketQuantity, priceTypeId) => {
        //    dispatch(selectPriceTypeTickets(+priceTypeId, ticketQuantity,true, [], false));
        //});
        //}
        //}
        checkAndApplyPromo(newState, dispatch, json);
    }
}